import { FEEDBACK } from '@/config/feedback';
import { GAME } from '@/config/game';
import { PLAYER } from '@/config/player';
import { levelDamageMul, type Boss } from '@/bosses/boss-base';
import type { RunState } from '@/run/run-state';
import type { Rewards } from '@/run/rewards';
import type { World } from '@/scene/world';

/**
 * Hit routing for one fixed step: player shots → boss parts / minions,
 * friendly explosions (AoE), enemy shots + contact → the ship. Damage
 * dealt pays out through Rewards; damage taken goes to `hitPlayer`.
 */
export class Collisions {
  constructor(
    private readonly world: World,
    private readonly run: RunState,
    private readonly rewards: Rewards,
    private readonly hitPlayer: (damage: number) => void,
  ) {}

  /** Boss phase: boss + its minions are live targets. */
  bossPhase(boss: Boss): void {
    const { world, run, rewards } = this;
    const { player, bullets, minions } = world;
    const energyPerHit = PLAYER.energyPerHit + player.stats.energyBonus;
    let bossDied = false;

    // Boss-defined hit zones (single circle / serpent heads / ...)
    const { hits, dealt } = boss.absorbBullets(bullets);
    if (hits > 0) {
      player.addEnergy(energyPerHit * hits);
      run.stats.damageDealt += dealt;
      rewards.onDamageDealt(dealt, boss.x, boss.y);
      if (boss.dead) bossDied = true;
    }
    rewards.rollShieldDrops(boss);

    const killed = minions.collide(bullets, player.x, player.y, (dmg) => {
      player.addEnergy(energyPerHit);
      run.stats.damageDealt += dmg;
      rewards.onDamageDealt(dmg, boss.x, boss.y);
    });
    rewards.rewardMinionKills(killed);

    // Area damage from mortar/rocket/missile explosions — hits BOTH the boss
    // AND minions in the blast (docs/12 #5 fix: bombs no longer fizzle).
    for (const aoe of bullets.drainAoeEvents()) {
      if (!aoe.friendly) continue;
      this.blast(aoe.x, aoe.y, aoe.radius);
      if (!bossDied) {
        // The boss routes the blast to the part inside it; immune = 0 (X4/X5)
        const dealtAoe = boss.applyAoe(aoe.x, aoe.y, aoe.radius, aoe.damage);
        if (dealtAoe > 0) {
          run.stats.damageDealt += dealtAoe;
          rewards.onDamageDealt(dealtAoe, aoe.x, aoe.y);
          // Merges into the hurt part's own number stream (docs/12 E1)
          const at = boss.aoeAt;
          world.damageNumbers.spawnHit(at.x, at.y - at.r, dealtAoe, false);
        }
        if (boss.dead) bossDied = true;
      }
      // Fly death sprays aim at the SHIP, as in the wave phase (not the boss)
      this.blastMinions(aoe.x, aoe.y, aoe.radius, aoe.damage, player.x, player.y);
    }

    // Enemy bullets vs player + contact damage — strongest source this frame.
    // Boss-fired sources scale with the boss level (docs/12 C6).
    const dmgMul = levelDamageMul(boss.level);
    const hitbox = PLAYER.radius * PLAYER.hitboxScale;
    let incoming = 0;
    bullets.collideCircle(false, player.x, player.y, hitbox, (dmg) => {
      incoming = Math.max(incoming, Math.round(dmg * GAME.enemyDamage.bulletUnit * dmgMul));
    });
    const touchingBoss =
      !bossDied && boss.touchesPlayer(player.x, player.y, PLAYER.radius * PLAYER.contactScale);
    if (touchingBoss) {
      incoming = Math.max(incoming, Math.round(GAME.enemyDamage.bossContact * dmgMul));
    }
    incoming = this.minionContact(hitbox, incoming);
    if (incoming > 0) {
      const landed = player.iframes <= 0;
      this.hitPlayer(incoming);
      // Kraken lifesteal hook (C7): only hits that actually landed feed it
      if (landed) boss.onDealtDamage(incoming);
    }

    // Deferred so it never mutates bullet arrays inside collide callbacks
    if (bossDied) rewards.onBossDeath(boss);
  }

  /** Wave phase (docs/12 C8): the boss is held offstage — minions only. */
  wavePhase(): void {
    const { world, run, rewards } = this;
    const { player, bullets, minions } = world;
    const energyPerHit = PLAYER.energyPerHit + player.stats.energyBonus;
    const killed = minions.collide(bullets, player.x, player.y, (dmg) => {
      player.addEnergy(energyPerHit);
      run.stats.damageDealt += dmg;
      rewards.onDamageDealt(dmg, player.x, player.y);
    });
    rewards.rewardMinionKills(killed);
    // Bomb AoE clears clustered minions in wave mode too (docs/12 #5 fix)
    for (const aoe of bullets.drainAoeEvents()) {
      if (!aoe.friendly) continue;
      this.blast(aoe.x, aoe.y, aoe.radius);
      this.blastMinions(aoe.x, aoe.y, aoe.radius, aoe.damage, player.x, player.y);
    }
    const hitbox = PLAYER.radius * PLAYER.hitboxScale;
    let incoming = 0;
    bullets.collideCircle(false, player.x, player.y, hitbox, (dmg) => {
      incoming = Math.max(incoming, dmg * GAME.enemyDamage.bulletUnit);
    });
    incoming = this.minionContact(hitbox, incoming);
    if (incoming > 0) this.hitPlayer(incoming);
  }

  private blast(x: number, y: number, radius: number): void {
    this.world.fx.explosion(x, y, radius / FEEDBACK.aoe.blastPerRadius);
    this.world.camera.shake(FEEDBACK.aoe.shake);
  }

  /** AoE vs minions; one merged number at the blast. (sx, sy) = fly death-spray aim. */
  private blastMinions(
    x: number, y: number, radius: number, damage: number, sx: number, sy: number,
  ): void {
    const { world, run } = this;
    let hitSomething = false;
    const killed = world.minions.damageCircle(
      world.bullets, x, y, radius, damage,
      (dmg) => { hitSomething = true; run.stats.damageDealt += dmg; }, sx, sy,
    );
    this.rewards.rewardMinionKills(killed);
    if (hitSomething) {
      world.damageNumbers.spawnHit(x, y - FEEDBACK.aoe.numberLift, damage, false);
    }
  }

  /** Minion bodies vs the ship: rammers die, the strongest hit wins. */
  private minionContact(hitbox: number, incoming: number): number {
    const { player, minions, fx } = this.world;
    const touch = minions.touchPlayer(player.x, player.y, hitbox);
    for (const d of touch.died) fx.explosion(d.x, d.y, FEEDBACK.minionBlast);
    return touch.hit ? Math.max(incoming, GAME.enemyDamage.minionTouch) : incoming;
  }
}
